import { useTranslation } from 'react-i18next';
import { ChevronRight, Menu, Search } from 'lucide-react';
import { ClientLogo } from '@/components/client-logo';
import { LanguageMenu } from '@/components/language-switcher';
import { ThemeToggle } from '@/components/theme-toggle';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useIsCompact } from '@/hooks/use-media-query';
import { useSidebarStore } from './sidebar.store';
import { useActiveNavItem } from './use-navigation';
import { UserMenu } from './user-menu';

/**
 * Sticky top bar: where you are on the left, who you are on the right.
 *
 * The old header carried a hamburger that toggled the desktop sidebar, a logo,
 * two language buttons and an avatar — and said nothing about the current page,
 * so the only way to know where you were was to read the URL.
 */
export function AppHeader({ onOpenSearch }: { onOpenSearch: () => void }) {
  const isCompact = useIsCompact();
  const setMobileOpen = useSidebarStore((s) => s.setMobileOpen);
  const active = useActiveNavItem();
  const { t } = useTranslation();

  return (
    <header className="bg-background/85 supports-[backdrop-filter]:bg-background/65 sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b px-3 backdrop-blur sm:px-4">
      {isCompact && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileOpen(true)}
          aria-label={t('shell.openNavigation')}
        >
          <Menu className="size-5" />
        </Button>
      )}

      {/* The client's logo, as the old header had it. Hidden below sm, where
          the breadcrumb and the controls already fill the bar. */}
      <ClientLogo className="hidden h-7 max-w-[9rem] shrink-0 sm:block" fallbackClassName="hidden sm:flex size-7" />

      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1.5">
        {active?.sectionLabel && !isCompact && (
          <>
            <span className="text-muted-foreground text-sm">
              {t(active.sectionLabel)}
            </span>
            <ChevronRight className="text-muted-foreground/50 size-3.5 shrink-0" />
          </>
        )}
        {active?.parentLabel && !isCompact && (
          <>
            <span className="text-muted-foreground text-sm">
              {t(active.parentLabel)}
            </span>
            <ChevronRight className="text-muted-foreground/50 size-3.5 shrink-0" />
          </>
        )}
        <h1 className="truncate text-sm font-semibold">
          {active ? t(active.label) : t('common.appName')}
        </h1>
      </nav>

      <div className="ml-auto flex items-center gap-1 sm:gap-2">
        {isCompact && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onOpenSearch}
            aria-label={t('shell.searchPages')}
          >
            <Search className="size-4" />
          </Button>
        )}
        {/* Both controls also live in the avatar menu, which is why they can
            drop off the bar entirely on a phone rather than crowding the
            breadcrumb down to nothing. */}
        <ThemeToggle className="hidden sm:inline-flex" />
        <LanguageMenu className="hidden sm:inline-flex" />
        <Separator orientation="vertical" className="hidden h-5 sm:block" />
        <UserMenu />
      </div>
    </header>
  );
}
